import Papa from 'papaparse';

/**
 * Export data to CSV file with RFC 4180 compliance
 * @param {Array<Object>} data - Array of objects to export
 * @param {string} filename - Name of the file to download
 * @param {Array<string>} columns - Optional array of column names to include (in order)
 */
export const exportToCSV = (data, filename, columns = null) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    throw new Error('Data must be a non-empty array');
  }

  // Determine columns to export
  const columnsToExport = columns || Object.keys(data[0]);

  // Filter data to only include specified columns
  const filteredData = data.map(row => {
    const filteredRow = {};
    columnsToExport.forEach(col => {
      filteredRow[col] = row[col] !== undefined ? row[col] : '';
    });
    return filteredRow;
  });

  // Convert to CSV using papaparse with RFC 4180 compliance
  const csv = Papa.unparse(filteredData, {
    quotes: true, // Quote all fields for safety
    quoteChar: '"',
    escapeChar: '"',
    delimiter: ',',
    header: true,
    newline: '\r\n', // RFC 4180 specifies CRLF
    skipEmptyLines: false,
  });

  // Add UTF-8 BOM for Excel compatibility
  const BOM = '\uFEFF';
  const csvWithBOM = BOM + csv;

  // Create blob and trigger download
  const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Parse CSV file content into array of objects
 * @param {string|File} input - CSV string or File object
 * @returns {Promise<Array<Object>>} - Promise resolving to array of parsed objects
 */
export const parseCSV = (input) => {
  return new Promise((resolve, reject) => {
    const config = {
      header: true, // First row is header
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(), // Trim whitespace from headers
      transform: (value) => value.trim(), // Trim whitespace from values
      complete: (results) => {
        if (results.errors && results.errors.length > 0) {
          reject(new Error(`CSV parsing errors: ${results.errors.map(e => e.message).join(', ')}`));
        } else {
          resolve(results.data);
        }
      },
      error: (error) => {
        reject(error);
      }
    };

    if (typeof input === 'string') {
      Papa.parse(input, config);
    } else if (input instanceof File) {
      Papa.parse(input, config);
    } else {
      reject(new Error('Input must be a string or File object'));
    }
  });
};
